import React, { useId } from "react";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";

export type AssignmentFeedbackItem = {
  title: string;
  grade: number;
  feedback?: string;
  submittedAt?: string | null;
};

export type AssignmentProgressBubblyStats = {
  total: number;
  submitted: number;
  reviewed: number;
  recentFeedback: AssignmentFeedbackItem[];
};

/** Matches header / Communication Can-Do / Tutor lesson chrome */
const VIEW_ALL_GRADIENT =
  "bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75 hover:brightness-110 shadow-lg shadow-purple-500/25";

function CircularScoreRing({ percent }: { percent: number }) {
  const uid = useId().replace(/:/g, "");
  const safe = Math.min(100, Math.max(0, percent));
  const r = 34;
  const c = 2 * Math.PI * r;
  const dash = (safe / 100) * c;

  return (
    <div className="relative flex h-[92px] w-[92px] shrink-0 items-center justify-center">
      <svg width={92} height={92} viewBox="0 0 92 92" className="-rotate-90" aria-hidden>
        <defs>
          <linearGradient id={`score-ring-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="45%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
        </defs>
        <circle
          cx="46"
          cy="46"
          r={r}
          fill="none"
          className="stroke-slate-200 dark:stroke-white/15"
          strokeWidth="8"
        />
        <circle
          cx="46"
          cy="46"
          r={r}
          fill="none"
          stroke={`url(#score-ring-${uid})`}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-xl font-black tabular-nums leading-none text-slate-900 dark:text-white">
          {Math.round(safe)}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-white/55">
          / 100
        </span>
      </div>
    </div>
  );
}

function StatBubble({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "indigo" | "violet" | "pink";
}) {
  const ring =
    variant === "indigo"
      ? "from-indigo-400 via-indigo-500 to-violet-600 shadow-[0_8px_28px_-10px_rgba(99,102,241,0.38)] dark:shadow-[0_12px_36px_-8px_rgba(129,140,248,0.48)]"
      : variant === "violet"
        ? "from-violet-400 via-app-primary/90 to-purple-700 shadow-[0_8px_28px_-10px_rgba(139,92,246,0.38)] dark:shadow-[0_12px_36px_-8px_rgba(168,85,247,0.45)]"
        : "from-fuchsia-400 via-app-primary/90 to-app-primary/75 shadow-[0_8px_28px_-10px_rgba(217,70,239,0.35)] dark:shadow-[0_12px_36px_-8px_rgba(236,72,153,0.42)]";

  return (
    <div className={`relative flex min-w-[7.5rem] flex-1 rounded-[1.75rem] bg-gradient-to-br p-[3px] ${ring}`}>
      <div className="flex w-full flex-col items-center justify-center rounded-[1.6rem] bg-white/95 px-4 py-4 backdrop-blur-sm dark:bg-gray-800/95">
        <p className="text-3xl font-black tabular-nums leading-none text-slate-900 dark:text-white">{value}</p>
        <p className="mt-1.5 text-center text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600 dark:text-white/75">
          {label}
        </p>
      </div>
    </div>
  );
}

function BubbleConnector() {
  return (
    <div className="hidden shrink-0 items-center justify-center px-1 sm:flex" aria-hidden>
      <div className="flex gap-1">
        <span className="h-2 w-2 rounded-full bg-indigo-500 dark:bg-indigo-400" />
        <span className="h-2 w-2 rounded-full bg-purple-500 dark:bg-purple-400" />
        <span className="h-2 w-2 rounded-full bg-pink-500 dark:bg-pink-400" />
      </div>
    </div>
  );
}

export type AssignmentProgressBubblyProps = {
  stats: AssignmentProgressBubblyStats;
  onViewAll: () => void;
  className?: string;
};

/**
 * Bubbly assignment summary — colors aligned with app chrome (indigo / purple / pink), light + dark.
 */
export function AssignmentProgressBubbly({ stats, onViewAll, className }: AssignmentProgressBubblyProps) {
  const { t } = useTranslation();
  const { total, submitted, reviewed, recentFeedback } = stats;

  return (
    <section
      className={clsx(
        "mt-8 rounded-[1.75rem] border border-purple-200 bg-white p-5 shadow-xl shadow-purple-200/35 ring-1 ring-slate-200/80 dark:border-purple-500/30 dark:bg-gray-800 dark:shadow-[0_24px_64px_-24px_rgba(147,51,234,0.35)] dark:ring-white/5 sm:p-7",
        className,
      )}
    >
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-50 p-3 ring-1 ring-purple-200/80 dark:from-indigo-500/25 dark:via-app-primary/90/20 dark:to-pink-600/15 dark:ring-purple-400/35">
            <FileText className="h-7 w-7 text-app-primary dark:text-indigo-300" aria-hidden />
          </div>
          <div>
            <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl dark:text-white">
              {t("dashboard.studentHome.assignments.title")}
            </h2>
            <p className="text-xs font-medium text-app-primary/85 dark:text-purple-200/75">
              {t("dashboard.studentHome.assignments.subtitle")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className={`inline-flex shrink-0 items-center justify-center rounded-full px-5 py-2.5 text-sm font-bold text-white transition hover:scale-[1.03] active:scale-[0.98] ${VIEW_ALL_GRADIENT}`}
        >
          {t("dashboard.studentHome.assignments.viewAll")}
        </button>
      </header>

      <div className="mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center">
        <StatBubble label={t("dashboard.studentHome.assignments.totalAssigned")} value={total} variant="indigo" />
        <BubbleConnector />
        <StatBubble label={t("dashboard.studentHome.assignments.submitted")} value={submitted} variant="violet" />
        <BubbleConnector />
        <StatBubble label={t("dashboard.studentHome.assignments.reviewed")} value={reviewed} variant="pink" />
      </div>

      {recentFeedback.length > 0 ? (
        <>
          <h3 className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-app-primary dark:text-fuchsia-300/90">
            {t("dashboard.studentHome.assignments.recentFeedback")}
          </h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recentFeedback.map((item, i) => (
              <article
                key={`${item.title}-${i}`}
                className="group flex flex-col rounded-[1.5rem] border border-slate-200/90 bg-slate-50/90 p-4 shadow-md shadow-slate-200/50 backdrop-blur-xl transition duration-300 hover:z-[1] hover:scale-[1.02] hover:border-purple-300 hover:bg-white hover:shadow-lg hover:shadow-purple-200/40 dark:border-white/12 dark:bg-white/[0.07] dark:shadow-black/25 dark:hover:border-purple-400/40 dark:hover:bg-white/[0.1] dark:hover:shadow-purple-500/20"
              >
                <div className="flex gap-3">
                  <CircularScoreRing percent={item.grade} />
                  <div className="min-w-0 flex-1 pt-1">
                    <h4 className="text-sm font-bold leading-snug text-slate-900 line-clamp-3 dark:text-white">
                      {item.title}
                    </h4>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-white/45">
                      {t("dashboard.studentHome.assignments.latestScore")}
                    </p>
                  </div>
                </div>
                {item.feedback ? (
                  <div className="mt-4 rounded-2xl border border-purple-200 bg-purple-50/95 px-3 py-2.5 backdrop-blur-sm dark:border-purple-400/30 dark:bg-purple-500/[0.12]">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-app-primary dark:text-purple-200/90">
                      {t("dashboard.studentHome.assignments.teacherNote")}
                    </p>
                    <p className="mt-1 text-sm italic leading-relaxed text-slate-800 dark:text-purple-50/95">
                      &ldquo;{item.feedback}&rdquo;
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white px-3 py-2 text-center text-xs text-slate-500 dark:border-white/15 dark:bg-white/[0.04] dark:text-white/40">
                    {t("dashboard.studentHome.assignments.noWrittenFeedback")}
                  </div>
                )}
              </article>
            ))}
          </div>
        </>
      ) : (
        <p className="mt-8 rounded-2xl border border-dashed border-purple-300 bg-purple-50/60 px-4 py-8 text-center text-sm text-slate-600 dark:border-purple-500/35 dark:bg-white/[0.04] dark:text-white/55">
          {t("dashboard.studentHome.assignments.submitToSee")}
        </p>
      )}
    </section>
  );
}
